import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"; // eslint-disable-line no-unused-vars
import CollapsibleCard from "../../components/CollapsibleCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Code, CheckCircle2 } from "lucide-react";

/**
 * BYO (Bring-Your-Own) online payments card. Self-contained: fetches its
 * own config on mount, owns all its form state, and posts/tests via its own
 * handlers. Parent (Settings.jsx) just renders <PaymentsCard /> when the
 * current user is an admin.
 *
 * Extracted from Settings.jsx in iter-70.
 */
const PaymentsCard = () => {
  const [paymentsConfig, setPaymentsConfig] = useState(null);
  const [paymentsForm, setPaymentsForm] = useState({
    enabled: false,
    provider: "stripe",
    stripe_secret_key: "",
    stripe_publishable_key: "",
    stripe_webhook_secret: "",
    sumup_api_key: "",
    sumup_merchant_code: "",
  });
  const [savingPayments, setSavingPayments] = useState(false);
  const [testingPayments, setTestingPayments] = useState(false);
  const [paymentsTestResult, setPaymentsTestResult] = useState(null);

  useEffect(() => {
    fetchPaymentsConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentsConfig = async () => {
    try {
      const res = await apiClient.get("/band/payments/config");
      setPaymentsConfig(res.data);
      setPaymentsForm((prev) => ({
        ...prev,
        enabled: !!res.data.enabled,
        provider: res.data.provider || "stripe",
      }));
    } catch {
      setPaymentsConfig(null);
    }
  };

  const savePaymentsConfig = async () => {
    setSavingPayments(true);
    setPaymentsTestResult(null);
    try {
      const provider = paymentsForm.provider || "stripe";
      const payload = { enabled: paymentsForm.enabled, provider };
      if (provider === "stripe") {
        if (paymentsForm.stripe_secret_key.trim())
          payload.stripe_secret_key = paymentsForm.stripe_secret_key.trim();
        if (paymentsForm.stripe_publishable_key.trim())
          payload.stripe_publishable_key = paymentsForm.stripe_publishable_key.trim();
        if (paymentsForm.stripe_webhook_secret.trim())
          payload.stripe_webhook_secret = paymentsForm.stripe_webhook_secret.trim();
      } else if (provider === "sumup") {
        if (paymentsForm.sumup_api_key.trim())
          payload.sumup_api_key = paymentsForm.sumup_api_key.trim();
        if (paymentsForm.sumup_merchant_code.trim())
          payload.sumup_merchant_code = paymentsForm.sumup_merchant_code.trim();
      }
      await apiClient.put("/band/payments/config", payload);
      toast.success("Online payments saved");
      setPaymentsForm((prev) => ({
        ...prev,
        stripe_secret_key: "",
        stripe_publishable_key: "",
        stripe_webhook_secret: "",
        sumup_api_key: "",
      }));
      await fetchPaymentsConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingPayments(false);
    }
  };

  const testPaymentsConnection = async () => {
    setTestingPayments(true);
    setPaymentsTestResult(null);
    try {
      const res = await apiClient.post("/band/payments/test");
      setPaymentsTestResult(res.data);
      const providerName = paymentsConfig?.provider === "sumup" ? "SumUp" : "Stripe";
      if (res.data.ok) toast.success(`Connection works — ${providerName} accepted the key.`);
      else toast.error(`${providerName} rejected the key. See details below.`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Test failed";
      setPaymentsTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTestingPayments(false);
    }
  };

  return (
    <CollapsibleCard icon={CreditCard} iconColor="text-emerald-400" title="Online Payments — Pay Now Links" className="border-emerald-500/20" testId="byo-payments-card">
        <p className="text-sm text-muted-foreground mb-4">
          Connect your band&apos;s own Stripe or SumUp account so members can pay invoices online by card.
          Funds go straight to your account — Vivo never touches the money.
        </p>
        <div className="space-y-5">
        {/* Provider selector */}
        <div>
          <Label className="text-sm font-medium">Payment provider</Label>
          <div className="flex gap-2 mt-2" data-testid="byo-provider-selector">
            {[
              { id: "stripe", label: "Stripe", help: "Global, sk_live_…" },
              { id: "sumup", label: "SumUp", help: "UK-friendly, lower fees" },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPaymentsForm({ ...paymentsForm, provider: p.id })}
                className={`flex-1 rounded-lg border px-4 py-3 text-left transition ${
                  paymentsForm.provider === p.id
                    ? "border-emerald-400/60 bg-emerald-500/10"
                    : "border-white/10 bg-secondary/40 hover:border-white/20"
                }`}
                data-testid={`byo-provider-${p.id}`}
              >
                <div className="font-medium">{p.label}</div>
                <div className="text-xs text-muted-foreground">{p.help}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Status row */}
        {paymentsConfig && (
          <div
            className={`rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap ${
              paymentsConfig.enabled
                ? "bg-emerald-500/10 border border-emerald-500/20"
                : "bg-secondary/40 border border-white/5"
            }`}
            data-testid="byo-payments-status"
          >
            <div className="flex items-center gap-2 text-sm">
              {paymentsConfig.enabled ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <Code className="w-4 h-4 text-muted-foreground" />
              )}
              <span>
                {paymentsConfig.enabled
                  ? `Connected via ${(paymentsConfig.provider || "stripe").toUpperCase()}${paymentsConfig.test_mode ? " · TEST MODE" : ""}`
                  : "Not connected — paste your keys below"}
              </span>
            </div>
            {paymentsConfig.provider === "sumup"
              ? paymentsConfig.sumup_api_key_masked && (
                  <span className="text-xs font-mono text-muted-foreground">
                    key: {paymentsConfig.sumup_api_key_masked}
                    {paymentsConfig.sumup_merchant_code ? ` · ${paymentsConfig.sumup_merchant_code}` : ""}
                  </span>
                )
              : paymentsConfig.stripe_secret_key_masked && (
                  <span className="text-xs font-mono text-muted-foreground">
                    secret: {paymentsConfig.stripe_secret_key_masked}
                  </span>
                )}
          </div>
        )}

        {paymentsForm.provider === "sumup" ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="byo-sumup-key">SumUp API Key (sk_live_… / sk_test_…)</Label>
              <Input
                id="byo-sumup-key"
                type="password"
                autoComplete="new-password"
                value={paymentsForm.sumup_api_key}
                onChange={(e) => setPaymentsForm({ ...paymentsForm, sumup_api_key: e.target.value })}
                placeholder={paymentsConfig?.sumup_api_key_masked || "sk_live_..."}
                className="font-mono"
                data-testid="byo-sumup-api-key-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get yours at{" "}
                <a href="https://me.sumup.com/settings/developer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  me.sumup.com/settings/developer
                </a>
                . Leave blank to keep the existing key.
              </p>
            </div>
            <div>
              <Label htmlFor="byo-sumup-mc">SumUp Merchant Code</Label>
              <Input
                id="byo-sumup-mc"
                value={paymentsForm.sumup_merchant_code}
                onChange={(e) => setPaymentsForm({ ...paymentsForm, sumup_merchant_code: e.target.value })}
                placeholder={paymentsConfig?.sumup_merchant_code || "MC123456"}
                className="font-mono"
                data-testid="byo-sumup-merchant-code-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your merchant code (e.g. <code className="text-primary text-xs">MC123456</code>) — find it on the same SumUp developer page.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="byo-sk">Stripe Secret Key (sk_live_… / sk_test_…)</Label>
              <Input
                id="byo-sk"
                type="password"
                autoComplete="new-password"
                value={paymentsForm.stripe_secret_key}
                onChange={(e) => setPaymentsForm({ ...paymentsForm, stripe_secret_key: e.target.value })}
                placeholder={paymentsConfig?.stripe_secret_key_masked || "sk_live_..."}
                className="font-mono"
                data-testid="byo-stripe-secret-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Get yours at{" "}
                <a
                  href="https://dashboard.stripe.com/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  dashboard.stripe.com/apikeys
                </a>
                . Leave blank to keep the existing key.
              </p>
            </div>

            <div>
              <Label htmlFor="byo-pk">Stripe Publishable Key (optional)</Label>
              <Input
                id="byo-pk"
                value={paymentsForm.stripe_publishable_key}
                onChange={(e) => setPaymentsForm({ ...paymentsForm, stripe_publishable_key: e.target.value })}
                placeholder={paymentsConfig?.stripe_publishable_key_masked || "pk_live_..."}
                className="font-mono"
                data-testid="byo-stripe-publishable-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used to display branding. Not required for accepting payments.
              </p>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="byo-wh">Webhook Signing Secret (optional, recommended)</Label>
              <Input
                id="byo-wh"
                type="password"
                autoComplete="new-password"
                value={paymentsForm.stripe_webhook_secret}
                onChange={(e) => setPaymentsForm({ ...paymentsForm, stripe_webhook_secret: e.target.value })}
                placeholder={paymentsConfig?.has_webhook_secret ? "•••••••• (set)" : "whsec_..."}
                className="font-mono"
                data-testid="byo-stripe-webhook-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Add a webhook in Stripe pointing at your domain's{" "}
                <code className="text-primary text-xs">/api/webhook/stripe/byo</code> endpoint. With this, paid invoices are marked paid instantly. Without it, Vivo polls Stripe right after the payment to confirm — still works, slight delay.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch
              checked={!!paymentsForm.enabled}
              onCheckedChange={(v) => setPaymentsForm({ ...paymentsForm, enabled: !!v })}
              data-testid="byo-payments-enabled-switch"
            />
            <span>Enable Pay Now button on invoices</span>
          </label>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
          <Button
            onClick={savePaymentsConfig}
            disabled={savingPayments}
            data-testid="byo-payments-save-btn"
          >
            {savingPayments ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outline"
            onClick={testPaymentsConnection}
            disabled={
              testingPayments ||
              (paymentsConfig?.provider === "sumup"
                ? !paymentsConfig?.sumup_api_key_masked
                : !paymentsConfig?.stripe_secret_key_masked)
            }
            data-testid="byo-payments-test-btn"
          >
            {testingPayments ? "Testing..." : "Test connection"}
          </Button>
          {paymentsTestResult && (
            <span
              className={`text-xs ml-auto ${paymentsTestResult.ok ? "text-emerald-400" : "text-red-400"}`}
              data-testid="byo-payments-test-result"
            >
              {paymentsTestResult.ok
                ? `✓ ${paymentsConfig?.provider === "sumup" ? "SumUp" : "Stripe"} accepted the key`
                : `✗ ${paymentsTestResult.error || "rejected"}`}
            </span>
          )}
        </div>
        </div>
    </CollapsibleCard>
  );
};

export default PaymentsCard;
